import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, TextField, Button, Box, Typography, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, Tooltip, Chip, Alert,
  Autocomplete, TableSortLabel, Collapse, Badge, FormControlLabel, Checkbox,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Save from '@mui/icons-material/Save';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import DescriptionIcon from '@mui/icons-material/Description';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import FileDropField from './FileDropField';

/**
 * Tabla editable reutilizable.
 *
 * Props:
 * - title: string - Titulo de la seccion
 * - columns: Array<{field, header, width?, type?, options?}> - Definicion de columnas
 * - rows: Array<object> - Datos
 * - onAdd: (newRow) => void
 * - onUpdate: (id, updatedRow) => void
 * - onDelete: (id) => void
 * - dialogFields: Array<{field, label, type?, options?, multiline?, rows?, required?, disabled?, helperText?}>
 *   - Campos del dialog. Tambien puede ser una funcion `(item) => Array<...>` (item es
 *     null al anadir), para variar un campo -p.ej. deshabilitarlo- segun el propio dato.
 * - emptyMessage: string
 */
export default function EditableTable({
  title,
  columns,
  rows = [],
  onAdd,
  onUpdate,
  onDelete,
  dialogFields,
  emptyMessage = 'No hay elementos. Haz clic en + para anadir.',
  getRowSx,
  notesField,
  hideLegend = false,
  renderExtraContent,
  collapsible = false,
  defaultCollapsed = false,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  summaryBanner,
  headerExtra,
  documentLinkField,
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [notesViewOpen, setNotesViewOpen] = useState(false);
  const [notesViewContent, setNotesViewContent] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // dialogFields puede ser el array de siempre, o una funcion del item que se
  // esta editando (null si es "Anadir"), para poder deshabilitar un campo segun
  // el propio dato -- p.ej. un importe que se recalcula solo y no admite edicion
  // manual en ciertos casos.
  const resolvedDialogFields = typeof dialogFields === 'function'
    ? dialogFields(editingItem)
    : dialogFields;

  const getEffectiveRowSx = (row) => {
    const base = getRowSx ? getRowSx(row) : {};
    if (notesField && row[notesField]) {
      return { ...base, backgroundColor: '#fff3e0', cursor: 'pointer' };
    }
    return Object.keys(base).length ? base : undefined;
  };

  // El doble clic edita. Las anotaciones se abren con su icono, no con el clic
  // simple: su ventana se tragaba el segundo clic y la edicion no llegaba a abrirse.
  const openNotes = (row) => {
    setNotesViewContent(row[notesField]);
    setNotesViewOpen(true);
  };

  const handleRowDoubleClick = (row) => {
    // El doble clic del navegador selecciona la palabra de debajo; se limpia para
    // que no quede texto resaltado detras del dialogo. La tabla sigue siendo
    // seleccionable con el raton, que hace falta para copiar valores.
    window.getSelection()?.removeAllRanges();
    handleOpenEdit(row);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedRows = sortField
    ? [...rows].sort((a, b) => {
        const va = a[sortField] ?? '';
        const vb = b[sortField] ?? '';
        const cmp = typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), 'es', { numeric: true });
        return sortDir === 'desc' ? -cmp : cmp;
      })
    : rows;

  const handleOpenNew = () => {
    const fields = typeof dialogFields === 'function' ? dialogFields(null) : dialogFields;
    const emptyForm = {};
    fields.forEach((f) => {
      emptyForm[f.field] = f.defaultValue !== undefined ? f.defaultValue : '';
    });
    setForm(emptyForm);
    setFormError('');
    setEditingItem(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (item) => {
    const fields = typeof dialogFields === 'function' ? dialogFields(item) : dialogFields;
    const filledForm = {};
    fields.forEach((f) => {
      if (f.type === 'date' && item[f.field]) {
        filledForm[f.field] = String(item[f.field]).slice(0, 10);
      } else {
        filledForm[f.field] = item[f.field] ?? '';
      }
    });
    setForm(filledForm);
    setFormError('');
    setEditingItem(item);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Los campos deshabilitados no se reenvian: su valor en el formulario es el que
    // habia al abrir el dialogo, y puede haber quedado obsoleto por una edicion hecha
    // mientras tanto en renderExtraContent (p.ej. el editor de partidas de una oferta).
    // Reenviarlo pisaria ese cambio mas reciente con el valor viejo.
    const disabledFields = new Set(
      resolvedDialogFields.filter((f) => f.disabled).map((f) => f.field)
    );
    // Clean form: convert empty strings to null so backend Optional fields accept them
    const cleanedForm = {};
    Object.entries(form).forEach(([key, value]) => {
      if (disabledFields.has(key)) return;
      cleanedForm[key] = value === '' ? null : value;
    });
    try {
      setFormError('');
      if (editingItem) {
        await onUpdate(editingItem.id, cleanedForm);
      } else {
        await onAdd(cleanedForm);
      }
      setDialogOpen(false);
    } catch (err) {
      console.error('Error saving:', err);
      const msg = err.response?.data?.detail
        || (Array.isArray(err.response?.data?.detail)
          ? err.response.data.detail.map(e => e.msg).join(', ')
          : null)
        || 'Error al guardar. Revisa los campos obligatorios.';
      setFormError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const selectedSet = new Set(selectedIds);
  const allSelected = rows.length > 0 && rows.every((r) => selectedSet.has(r.id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleOne = (id) => {
    if (!onSelectionChange) return;
    onSelectionChange(
      selectedSet.has(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]
    );
  };

  const toggleAll = () => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? [] : rows.map((r) => r.id));
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmId !== null) {
      await onDelete(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  };

  const renderCellValue = (row, col) => {
    if (col.render) return col.render(row);
    const val = row[col.field];
    if (val === null || val === undefined || val === '') return '-';
    if (col.type === 'chip') {
      const chipColor = col.colorMap?.[val] || 'default';
      const chipLabel = col.labelMap?.[val] || val;
      return <Chip label={chipLabel} size="small" color={chipColor} />;
    }
    if (col.type === 'number') return Number(val).toLocaleString('es-ES');
    if (col.type === 'date') {
      try { return new Date(val).toLocaleDateString('es-ES'); } catch { return val; }
    }
    return val;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1,
          ...(collapsible ? { cursor: 'pointer', userSelect: 'none' } : {}),
        }}
        onClick={collapsible ? () => setCollapsed((c) => !c) : undefined}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {collapsible && (
            collapsed ? <ExpandMore color="action" /> : <ExpandLess color="action" />
          )}
          <Typography variant="h6">{title}</Typography>
          {collapsible && (
            <Chip
              size="small"
              label={rows.length === 0 ? 'vacio' : `${rows.length}`}
              color={rows.length > 0 ? 'primary' : 'default'}
              variant={rows.length > 0 ? 'filled' : 'outlined'}
              sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
            />
          )}
          {summaryBanner && rows.length > 0 && summaryBanner}
          {notesField && !hideLegend && !collapsed && (
            <Chip
              size="small"
              label="🟠 Naranja = hay anotaciones · doble clic en la fila para editar"
              sx={{ backgroundColor: '#fff3e0', fontSize: '0.72rem' }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
          {headerExtra}
          <Button size="small" variant="outlined" startIcon={<Add />} onClick={(e) => { e.stopPropagation(); handleOpenNew(); }}>
            Anadir
          </Button>
        </Box>
      </Box>

      <Collapse in={!collapsible || !collapsed}>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#c62828' }}>
              {selectable && (
                <TableCell padding="checkbox" sx={{ width: 42 }}>
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' }, '&.MuiCheckbox-indeterminate': { color: '#fff' } }}
                    inputProps={{ 'aria-label': 'Seleccionar todos' }}
                  />
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  sx={{ fontWeight: 'bold', color: '#fff', width: col.width || 'auto' }}
                  align={col.type === 'number' ? 'right' : 'left'}
                  sortDirection={sortField === col.field ? sortDir : false}
                >
                  <TableSortLabel
                    active={sortField === col.field}
                    direction={sortField === col.field ? sortDir : 'asc'}
                    onClick={() => handleSort(col.field)}
                    sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                  >
                    {col.header}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 'bold', color: '#fff', width: documentLinkField ? 160 : 130 }} align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (selectable ? 2 : 1)} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="textSecondary">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row, idx) => (
                <TableRow
                  key={row.id || idx}
                  hover
                  sx={{ ...(getEffectiveRowSx(row) || {}), cursor: 'pointer' }}
                  onDoubleClick={() => handleRowDoubleClick(row)}
                  title="Doble clic para editar"
                >
                  {selectable && (
                    <TableCell padding="checkbox"
                      onClick={(e) => e.stopPropagation()}
                      onDoubleClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selectedSet.has(row.id)}
                        onChange={() => toggleOne(row.id)}
                        inputProps={{ 'aria-label': `Seleccionar ${row.name || row.id}` }}
                      />
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell key={col.field} align={col.type === 'number' ? 'right' : 'left'}>
                      {renderCellValue(row, col)}
                    </TableCell>
                  ))}
                  <TableCell align="right"
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}>
                    {notesField && row[notesField] && (
                      <Tooltip title="Ver anotaciones">
                        <IconButton size="small" onClick={() => openNotes(row)}>
                          <StickyNote2Icon fontSize="small" sx={{ color: '#ef6c00' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {documentLinkField && row[documentLinkField] && (
                      <Tooltip title="Abrir documento de oferta">
                        <IconButton
                          size="small"
                          color="primary"
                          component="a"
                          href={row[documentLinkField]}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(row.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Collapse>

      {/* Dialog confirmacion de borrado */}
      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)} maxWidth="xs">
        <DialogTitle>Confirmar eliminacion</DialogTitle>
        <DialogContent>
          <Typography>¿Estas seguro de que deseas eliminar este elemento?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para ver anotaciones */}
      <Dialog open={notesViewOpen} onClose={() => setNotesViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Anotaciones</DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>{notesViewContent}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesViewOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para crear/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Editar' : 'Nuevo'} - {title}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {resolvedDialogFields.map((field) => (
              <Grid item xs={12} sm={field.fullWidth ? 12 : 6} key={field.field}>
                {field.type === 'autocomplete' ? (
                  <Autocomplete
                    options={field.options || []}
                    getOptionLabel={(opt) => (typeof opt === 'string' ? opt : (opt.label || ''))}
                    isOptionEqualToValue={(opt, val) =>
                      (typeof val === 'string' ? opt.label === val || opt.value === val : opt.value === val?.value)
                    }
                    value={
                      field.options?.find((o) => o.value === form[field.field] || o.label === form[field.field]) ||
                      (form[field.field] ? { value: form[field.field], label: form[field.field] } : null)
                    }
                    onChange={(e, val) =>
                      setForm({ ...form, [field.field]: val ? (typeof val === 'string' ? val : (val.label || val.value || '')) : '' })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label={field.label} required={field.required} fullWidth />
                    )}
                  />
                ) : field.type === 'file_drop' ? (
                  <FileDropField
                    value={form[field.field] || ''}
                    onChange={(val) => setForm({ ...form, [field.field]: val })}
                    label={field.label}
                    onUpload={field.onUpload ? (file) => field.onUpload(file, form) : undefined}
                  />
                ) : field.type === 'checkbox' ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!form[field.field]}
                        onChange={(e) => setForm({ ...form, [field.field]: e.target.checked })}
                      />
                    }
                    label={field.label}
                  />
                ) : field.type === 'select' ? (
                  <TextField
                    fullWidth
                    select
                    label={field.label}
                    name={field.field}
                    value={form[field.field] || ''}
                    onChange={handleChange}
                    required={field.required}
                    SelectProps={{ native: true }}
                  >
                    <option value="">-- Seleccionar --</option>
                    {field.options?.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    fullWidth
                    label={field.label}
                    name={field.field}
                    value={form[field.field] || ''}
                    onChange={handleChange}
                    type={field.type || 'text'}
                    multiline={field.multiline}
                    rows={field.rows || 1}
                    required={field.required}
                    disabled={field.disabled}
                    helperText={field.helperText}
                  />
                )}
              </Grid>
            ))}
          </Grid>
          {renderExtraContent && editingItem && renderExtraContent(editingItem)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} startIcon={<Save />}>
            {editingItem ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
